<script setup lang="ts">
import type { ContentItem } from "@edco/apollo";
import type { Home } from "~/models/generated/Home";
import type { EventItem } from "~/models/generated/EventItem";
import type { OnDemandEventItem } from "~/models/generated/OnDemandEventItem";
import EventCard from "~/components/events/EventCard.vue";
import { useMigratedSeo } from "~/composables/useMigratedSeo";

const props = defineProps<{
  content: ContentItem<Home>;
}>();

useMigratedSeo( () => props.content );

const contentResource = useContentDeliveryResource();
const auth = useAuthStore();

if ( auth.isLoggedIn ) {
  const myNea = await contentResource.getByTypeSingle( "myNea" );

  if ( myNea.data.value ) {
    await navigateTo( myNea.data.value.route.path );
  }
}

const registerContent = await contentResource.getByTypeSingle( "register" );

const registerUrl = computed( () => registerContent.data.value?.route.path );

const articleResource = useArticleResource();
const { data: promotedArticles } = await articleResource.getPromoted( 4 );

const PROMOTED_EVENTS_MAX = 4;
const PROMOTED_EVENTS_BACKFILL_FETCH = 8;

/**
 * Selected live/on demand events are backfilled with the most recently added events of that
 * type (mirroring how selected articles are backfilled) when fewer than the max are picked.
 */
async function getPromotedEvents<T extends EventItem | OnDemandEventItem> (
  contentTypeAlias: string,
  selected: ContentItem<T>[] | null | undefined,
  maxRecords = PROMOTED_EVENTS_MAX,
) {
  const selectedItems = ( selected ?? [] ).slice( 0, maxRecords );

  if ( selectedItems.length >= maxRecords ) {
    return selectedItems;
  }

  const { data } = await contentResource.getByType<T>( contentTypeAlias, {
    take: PROMOTED_EVENTS_BACKFILL_FETCH,
    sort: "createDate:desc",
  } );

  const selectedIds = new Set( selectedItems.map( item => item.id ) );
  const backfill = ( data.value?.items ?? [] ).filter( item => !selectedIds.has( item.id ) );

  return [...selectedItems, ...backfill].slice( 0, maxRecords );
}

const [promotedLiveEvents, promotedOnDemandEvents] = await Promise.all( [
  getPromotedEvents<EventItem>( "eventItem", props.content.properties.selectedLiveEvents as ContentItem<EventItem>[] | null ),
  getPromotedEvents<OnDemandEventItem>( "onDemandEventItem", props.content.properties.selectedOnDemandEvents as ContentItem<OnDemandEventItem>[] | null ),
] );

function toEventCardProps ( event: ContentItem<EventItem | OnDemandEventItem> ) {
  return {
    name: event.name,
    url: event.route.path,
    associatedAuthors: event.properties.associatedAuthors || null,
    multipleAuthors: ( event.properties.associatedAuthors?.length ?? 0 ) > 1,
    bannerImage: event.properties.bannerImage?.[0] || null,
    searchThumbnail: event.properties.searchThumbnail?.[0] || null,
    publishedDate: event.properties.publishedDate ? event.properties.publishedDate.toString() : null,
    topics: event.properties.topics || undefined,
    eventDate: event.properties.eventDate || null,
    eventTime: event.properties.eventTime || null,
    eventLocation: event.properties.eventLocation || null,
    authorIntroduction: event.properties.authorIntroduction || null,
  };
}

const splitHeading = ( text?: string | null ) => {
  const trimmed = text?.trim() ?? "";

  return trimmed.endsWith( "." )
    ? { base: trimmed.slice( 0, -1 ), fullStop: "." }
    : { base: trimmed, fullStop: "" };
};
</script>

<template>
  <main class="home-page">
    <page-banner
      v-if="content.properties.bannerToggle !== false"
      :content="content.properties"
      class="home-page__banner" />

    <section
      v-if="content.properties.whyJoinItems?.items?.length"
      class="section">
      <div class="container">
        <div class="benefits-section">
          <h2>{{ content.properties.whyJoinHeading || 'Benefits of joining the NEA' }}</h2>
          <rich-text
            v-if="content.properties.whyJoinIntro?.markup"
            :content="content.properties.whyJoinIntro.markup" />
          <div class="hsa-row">
            <div
              v-for="item in content.properties.whyJoinItems.items"
              :key="item.content.id"
              class="hsa-panel"
              :style="item.content.properties.image?.[0]?.url ? { backgroundImage: `url(${item.content.properties.image[0].url})` } : undefined">
              <p
                v-if="item.content.properties.subHeading"
                class="hsa-panel__subheading">
                {{ item.content.properties.subHeading }}
              </p>
              <h2>
                {{ splitHeading(item.content.properties.heading).base }}<span
                  v-if="splitHeading(item.content.properties.heading).fullStop"
                  class="hsa-panel__heading-dot">{{ splitHeading(item.content.properties.heading).fullStop }}</span>
              </h2>
              <p>
                {{ item.content.properties.description }}
              </p>
              <p v-if="item.content.properties.learnMoreLink?.url || item.content.properties.learnMoreLink?.route?.path">
                <a
                  :href="item.content.properties.learnMoreLink.route?.path || item.content.properties.learnMoreLink.url"
                  :target="item.content.properties.learnMoreLink.target ?? undefined"
                  class="hsa-btn">{{ item.content.properties.learnMoreLabel || 'Learn more' }}</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      v-if="promotedArticles?.length"
      class="section promoted-articles-section">
      <div class="container">
        <div class="promoted-content">
          <h2>
            {{ content.properties.selectedArticlesTitle || 'Featured articles for you' }}
          </h2>
          <p
            v-if="content.properties.selectedArticlesDescription"
            class="promoted-articles-description">
            {{ content.properties.selectedArticlesDescription }}
          </p>
          <div class="promoted-articles-grid">
            <article-card
              v-for="article in promotedArticles"
              :key="article.url"
              hide-action-icon
              v-bind="article" />
          </div>
        </div>
      </div>
    </section>

    <section
      v-if="promotedLiveEvents.length"
      class="section promoted-events-section">
      <div class="container">
        <div class="promoted-content">
          <h2>
            {{ content.properties.selectedLiveEventsTitle || 'Live events for you' }}
          </h2>
          <div class="promoted-events-grid">
            <event-card
              v-for="event in promotedLiveEvents"
              :key="event.id"
              v-bind="toEventCardProps(event)" />
          </div>
        </div>
      </div>
    </section>

    <section
      v-if="promotedOnDemandEvents.length"
      class="section promoted-events-section">
      <div class="container">
        <div class="promoted-content">
          <h2>
            {{ content.properties.selectedOnDemandEventsTitle || 'On demand events for you' }}
          </h2>
          <div class="promoted-events-grid">
            <event-card
              v-for="event in promotedOnDemandEvents"
              :key="event.id"
              v-bind="toEventCardProps(event)" />
          </div>
        </div>
      </div>
    </section>

    <section class="section join-section">
      <div class="container">
        <div class="join-cta">
          <div class="join-cta-left">
            <h2>{{ content.properties.joinHeading }}</h2>
            <rich-text
              v-if="content.properties.joinDescription?.markup"
              :content="content.properties.joinDescription.markup" />
            <p v-if="registerUrl">
              <a
                :href="registerUrl"
                class="join-cta-btn">{{ content.properties.joinButtonLabel || 'Join as a member' }}</a>
            </p>
          </div>
          <div class="join-cta-right" />
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped lang="scss">
@import '~/assets/styles/_variables.scss';

.home-page {
  margin: -20px auto -60px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 30px;

  .register {
    background: #fff;
    min-height: 300px;
    width: 100%;
    padding: 80px 20px;
    margin-top: 0;
    margin-bottom: 0;
    text-align: center;

    &__heading {
      display: flex;
      align-items: center;
      gap: 10px;
      justify-content: center;
      margin-bottom: 1rem;
      font-weight: 600;
      text-transform: uppercase;
    }

    &__actions {
      margin-top: 20px;
      display: flex;
      align-items: center;
      flex-direction: column;
      gap: 10px;
    }

    :deep( .registration-step__heading ) {
      text-transform: none;
      line-height: 1;
      font-size: 2rem;
    }

    :deep( .registration-step__info ) {
      margin-bottom: 20px;
    }

    .btn-primary {
      opacity: 1;
      //background-color: var( --color-accent-3 );
      font-size: 1.2rem;
      background-color: #62BBAD;
      border-color: #62BBAD;
    }
    .btn-primary:hover {
      opacity: 1;
      //background-color: var( --color-accent-3 );
      background-color: #E6007E;
      border-color: #E6007E;
    }
  }

}

.light-mode .home-page {
  background: #ffffff;
}

.join-cta {background: #201D25; border-radius: 15px;padding: 30px;margin-bottom: 50px;color: #fff;display: flex;gap: 30px;background-image:url('/public/join-cta-background.jpg');background-position:top 0 right -30px;background-size:60%;background-repeat:no-repeat;
  h2 {color: #fff;font-size: 2.5rem;font-weight: 500;}
  .join-cta-btn {background: #E6007E;border: 1px solid #E6007E;color: #fff;border-radius: 40px;padding: 12px 20px;text-decoration: none;margin-top: 20px;display: inline-block;
    &:hover {border: 1px solid #fff;}
  }

  }
.hsa-row {display: flex;margin:30px auto;align-items: stretch;justify-content: space-between;gap: 20px;}
.hsa-panel {padding: 30px;color: #fff; background: #201D25;flex: 1;border-radius: 15px;background-repeat: no-repeat;background-size: 20%;background-position: calc(100% - 30px) 30px;
  p {color: #fff;}
  }
.hsa-panel h2 {font-size: 3rem; color: #fff;font-weight: 500;padding:0;margin:0 0 30px 0;font-weight:400;}
.hsa-panel__heading-dot {color: $accent-2;}
.hsa-panel__subheading {font-size: 16px;text-transform: uppercase;margin: 0;padding: 0;}

.hsa-panel a.hsa-btn {color: #fff; border: 1px solid #fff;display:inline-block;padding: 12px 25px; margin-top: 15px; border-radius: 30px;text-decoration: none;
  &:hover {color: #fff;border-color: #fff;background: #E6007E;}
  }
.promoted-content {
  margin-bottom: 40px;
}
.promoted-articles-description {
  margin-bottom: 20px;
}
.promoted-articles-grid,
.promoted-events-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.article-card {
  background: #ffffff;
  border-radius: 7px;
  border: 1px solid #f2f2f2;
  display: flex;
  position: relative;
  flex-direction: column;
  text-decoration: none;
  flex-grow: 1;
  height: 100%;
  container-type: inline-size;
  transition: transform 0.2s ease-in, box-shadow 0.2s ease-in;

  &:hover {
    transform: translateY( -0.25rem );
  }

  &--shadow,
  &:hover {
    box-shadow: 0 5px 10px rgba(68, 68, 68, 0.25);
  }

  &__wrapper {
    display: flex;
    flex-direction: column-reverse;
    height: 100%;
  }

  &__image-wrapper {

    background: #555;
    position: relative;
    display: flex;
    min-height: 200px;
  }

  &__image {
    flex-grow: 1;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 7px 7px 0 0;
  }

  &__image-overlay {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    position: relative;
    z-index: 10;
    color: #fff;
    padding: 15px calc( 2.5rem + 20px ) 15px 15px;
    background-image: linear-gradient( 0deg,
      rgba( 27, 28, 34, 0.8 ) 0%,
      rgba( 27, 28, 34, 0.7 ) 15%,
      rgba( 27, 28, 34, 0.6 ) 33%,
      rgba( 27, 28, 34, 0 ) 100% );
    border-radius: 7px 7px 0 0;
    text-shadow: 0 0 4px #000000d1;
  }

  &__image-wrapper,
  &__image,
  &__image-overlay {
    border-radius: 6px 6px 0 0;
  }

  &__content-wrapper {
    overflow: hidden;
    position: relative;
    flex-grow: 1;
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: 15px;
    // color: var( --secondary );
    color: #201D25;
    height: 100%;
  }

  &__heading {
    font-size: 1.1rem;
    font-family: var( --font-family-base ), sans-serif;
    margin-bottom: 15px;

    &--xs {
      font-size: 0.8rem;
    }

    &--sm {
      font-size: 1rem;
    }

    &--lg {
      font-size: 1.25rem;
    }

    &--xl {
      font-size: 1.5rem;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__date,
  &__org {
    font-size: 0.8rem;
  }

  &__description {
    margin-top: -5px;
    margin-bottom: 15px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    line-height: 1.2;
  }

  &__footer {
    margin-top: auto;
  }

  &__tags {
    margin-bottom: 10px;
  }

  &__actions {
    border-top: 1px solid var( --primary );
    display: flex;
    justify-content: space-between;
    padding-top: 15px;
    align-items: center;
  }

  &__action,
  &__time {
    font-size: 0.875rem;
    line-height: 0.875rem;
  }

  &__action {
    svg {
      font-size: 0.5rem;
    }
  }

  &__author {
    word-break: break-word;
  }

  &__new {
    z-index: 11;
    position: absolute;
    top: 10px;
    left: 10px;
  }

  &__most-popular {
    position: absolute;
    top: -10px;
    right: 0;
    z-index: 60;
  }

  &__time {
    svg {
      display: inline-block;
      margin-right: 3px;
    }
  }

  &__stats {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 5px;
    background: #f6f9fc;
    padding: 7px 10px;
    font-size: 0.85rem;
    margin-bottom: 5px;
  }

  &__stat-number {
    font-weight: 600;
  }

  @container (min-width: 600px) {
    &__wrapper {
      flex-direction: row-reverse;
      justify-content: stretch;
    }

    &__image-wrapper,
    &__content-wrapper {
      flex-basis: 50%;
    }

    &__image-wrapper,
    &__image,
    &__image-overlay {
      border-radius: 7px 0 0 7px;
    }
  }
}

@media (max-width: 1080px) {
  .hsa-panel {
    padding-top: 65px;
  }
  .promoted-articles-grid,
  .promoted-events-grid {grid-template-columns: repeat(2, 1fr);}
}

@media (max-width: 768px) {
  .benefits-section {
    margin: 20px;
    .hsa-row {
      flex-direction: column;
      .hsa-panel {background-size: 15%;}
    }
  }
  .promoted-content {
    margin: auto 20px;
  }
  .promoted-articles-grid,
  .promoted-events-grid {grid-template-columns: repeat(1, 1fr);}
  .join-cta {
    margin:auto 20px;
    margin-bottom: 40px;
    background-image: none;
  }
}
.join_btn {
      margin-left: 20px;
    }
</style>
